import { Component, inject } from '@angular/core';
import {
  NgbCollapse,
  NgbCollapseModule,
  NgbDropdownModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SimplebarAngularModule } from 'simplebar-angular';
import { MENU_ITEMS } from '@core/config/menu.config';
import { MenuItemType } from '@common/models/menu-item.type';
import {
  basePath,
  currency,
  roleMasterUuid,
} from '@common/constants/constants';
import { findAllParent, findMenuItem } from '@core/helper/utils';
import { LogoBoxComponent } from '@common/components/logo-box.component';
import { Store } from '@ngrx/store';
import { getSidebarsize } from '@/store/layout/layout-selector';
import { changesidebarsize } from '@/store/layout/layout-action';
import { AuthFacade } from '@views/auth/facades/auth.facade';
import { IdentityFacade } from '@core/facades/identity.facade';
import { CurrentUserVm } from '@common/models/current-user/current-user.vm';
import { removeLastWord } from '@common/utils/string.util';
import {
  ROUTE_FORMS,
  ROUTE_ROLES_PERMISSIONS,
} from '@core/config/route-paths.config';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    NgbCollapseModule,
    SimplebarAngularModule,
    LogoBoxComponent,
    NgbDropdownModule,
  ],
  templateUrl: './sidebar.component.html',
  styles: ``,
})
export class SidebarComponent {
  currency = currency;
  menuItems: MenuItemType[] = [];
  activeMenuItems: string[] = [];
  currentUser: CurrentUserVm | null = null;

  router = inject(Router);
  authFacade = inject(AuthFacade);
  identityFacade = inject(IdentityFacade);

  trimmedURL = this.router.url?.replaceAll(
    basePath !== '' ? basePath + '/' : '',
    '/',
  );

  store = inject(Store);

  constructor() {
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this.trimmedURL = this.router.url?.replaceAll(
          basePath !== '' ? basePath + '/' : '',
          '/',
        );
        this._activateMenu();
        setTimeout(() => {
          this.scrollToActive();
        }, 200);
      }
    });
  }

  ngOnInit(): void {
    this.loadCurrentUser();
    this.initMenu();
  }

  initMenu(): void {
    let menuItems = MENU_ITEMS;
    const roleUuid = this.currentUser?.roleUuid;

    if (roleUuid != roleMasterUuid) {
      menuItems = menuItems.filter(
        (menuItem) =>
          menuItem.key != ROUTE_ROLES_PERMISSIONS &&
          menuItem.key != ROUTE_FORMS,
      );
    }

    this.menuItems = menuItems;
  }

  loadCurrentUser(): void {
    const snapshot = this.identityFacade.getSnapshot();
    if (!snapshot) return;

    snapshot.person.name = removeLastWord(snapshot.person.name);
    this.currentUser = snapshot;
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._activateMenu();
    });
    setTimeout(() => {
      this.scrollToActive();
    }, 200);
  }

  hasSubmenu(menu: MenuItemType): boolean {
    return menu.children ? true : false;
  }

  scrollToActive(): void {
    const activatedItem = document.querySelector('.side-nav-item li a.active');
    if (activatedItem) {
      const simplebarContent = document.querySelector(
        '.sidenav-menu .simplebar-content-wrapper',
      );
      if (simplebarContent) {
        const activatedItemRect = activatedItem.getBoundingClientRect();
        const simplebarContentRect = simplebarContent.getBoundingClientRect();
        const activatedItemOffsetTop =
          activatedItemRect.top + simplebarContent.scrollTop;
        const centerOffset =
          activatedItemOffsetTop -
          simplebarContentRect.top -
          simplebarContent.clientHeight / 2 +
          activatedItemRect.height / 2;
        this.scrollTo(simplebarContent, centerOffset, 600);
      }
    }
  }

  easeInOutQuad(t: number, b: number, c: number, d: number): number {
    t /= d / 2;
    if (t < 1) return (c / 2) * t * t + b;
    t--;
    return (-c / 2) * (t * (t - 2) - 1) + b;
  }

  scrollTo(element: Element, to: number, duration: number): void {
    const start = element.scrollTop;
    const change = to - start;
    const increment = 20;
    let currentTime = 0;

    const animateScroll = () => {
      currentTime += increment;
      const val = this.easeInOutQuad(currentTime, start, change, duration);
      element.scrollTop = val;
      if (currentTime < duration) {
        setTimeout(animateScroll, increment);
      }
    };
    animateScroll();
  }

  _activateMenu(): void {
    const div = document.querySelector('.sidenav-menu');

    let matchingMenuItem = null;

    if (div) {
      let items: any = div.getElementsByClassName('nav-link-ref');
      for (let i = 0; i < items.length; ++i) {
        if (
          this.trimmedURL === items[i].pathname ||
          (this.trimmedURL.startsWith('/invoice/') &&
            items[i].pathname === '/invoice/RB6985') ||
          (this.trimmedURL.startsWith('/ecommerce/product/') &&
            items[i].pathname === '/ecommerce/product/1')
        ) {
          matchingMenuItem = items[i];
          break;
        }
      }

      if (matchingMenuItem) {
        const mid = matchingMenuItem.getAttribute('aria-controls');
        const activeMt = findMenuItem(this.menuItems, mid);

        if (activeMt) {
          const matchingObjs = [
            activeMt['key'],
            ...findAllParent(this.menuItems, activeMt),
          ];

          this.activeMenuItems = matchingObjs;
          this.menuItems.forEach((menu: MenuItemType) => {
            menu.collapsed = !matchingObjs.includes(menu.key!);
          });
        }
      }
    }
  }

  /**
   * toggles open menu
   * @param menuItem clicked menuitem
   * @param collapse collpase instance
   */
  toggleMenuItem(menuItem: MenuItemType, collapse: NgbCollapse): void {
    collapse.toggle();
    let openMenuItems: string[];
    if (!menuItem.collapsed) {
      openMenuItems = [
        menuItem['key'],
        ...findAllParent(this.menuItems, menuItem),
      ];
      this.menuItems.forEach((menu: MenuItemType) => {
        if (!openMenuItems.includes(menu.key!)) {
          menu.collapsed = true;
        }
      });
    }
  }
  logout() {
    this.authFacade.logout();
  }

  changeSidebarSize() {
    let size = document.documentElement.getAttribute('data-sidenav-size');
    if (size == 'sm-hover') {
      size = 'sm-hover-active';
    } else {
      size = 'sm-hover';
    }
    this.store.dispatch(changesidebarsize({ size }));
    this.store.select(getSidebarsize).subscribe((size) => {
      document.documentElement.setAttribute('data-sidenav-size', size);
    });
  }
}
